/**
 * Action registry (DESIGN.md §18). An action is defined ONCE (id, label key, icon, shortcut,
 * predicates, run) and rendered by every surface that offers it: toolbar buttons, the "…"
 * overflow menu, the context menu (right-click, Shift+F10, long-press), the command palette and
 * keyboard shortcuts. Surfaces never define their own entries.
 *
 * This module is framework-free (no React) so the rules are unit-tested on their own.
 */
import type { MessageKey, MessageParams, Translate } from '@platform/i18n';
import type { LucideIcon } from 'lucide-react';
import type { ShortcutAction } from '../hooks/use-shortcut';

/** Where an action is offered. Shortcuts are not a surface: every action with `shortcut` has one. */
export type ActionSurface = 'toolbar' | 'overflow' | 'context' | 'palette';

/** How the action was invoked (surface or keyboard shortcut). */
export type ActionSource = ActionSurface | 'shortcut';

/** Default surfaces: menus and palette, no toolbar button. */
export const DEFAULT_SURFACES: readonly ActionSurface[] = ['overflow', 'context', 'palette'];

/** A pack key, or a key with ICU parameters (e.g. counts). */
export type LabelRef = MessageKey | { key: MessageKey; params: MessageParams };

export function labelText(translate: Translate, label: LabelRef): string {
  return typeof label === 'string' ? translate(label) : translate(label.key, label.params);
}

/**
 * How many targets an action takes:
 * - `one`  exactly one item (reply, rename)
 * - `many` one or more items (mark read, delete): offered for a multi-selection
 */
export type ActionArity = 'one' | 'many';

export interface ActionContext<T> {
  /** The items the action applies to: the selection, or the item right-clicked (after selecting it). */
  readonly targets: readonly T[];
  readonly source: ActionSource;
}

/** Confirmation before the action runs (DESIGN.md T8, shown with `ConfirmDialog`). */
export interface ActionConfirm {
  title: LabelRef;
  description: LabelRef;
  /** The verb, e.g. "Delete folder". */
  confirm: LabelRef;
}

/*
 * The callbacks use method syntax on purpose: methods are bivariant, so a `ResolvedAction<User>`
 * can travel through generic surfaces typed `ResolvedAction<unknown>`.
 */
export interface ActionDef<T> {
  /** Unique within its registry; equals the keymap id when the action has a shortcut (`mail.reply`). */
  id: string;
  label: LabelRef;
  /** Label depending on the targets ("Flag" / "Clear flag"); overrides `label`. */
  labelFor?(context: ActionContext<T>): LabelRef;
  icon?: LucideIcon;
  /** Keymap entry (`KEYMAP_OUTLOOK`); its chords run the action and are shown in menus. */
  shortcut?: ShortcutAction;
  /** Menus put a separator between groups; groups appear in registry order. */
  group: string;
  /** Destructive (red in menus). Pair with `confirm` unless the action is undoable. */
  danger?: boolean;
  /** Default `one`. */
  arity?: ActionArity;
  /** Default `DEFAULT_SURFACES`. */
  surfaces?: readonly ActionSurface[];
  /**
   * The item's own click/Enter does this action (open). It is shown first and bold in the context
   * menu and needs no other surface, because the click is the path without right-click.
   */
  defaultGesture?: boolean;
  /** Hidden when false (not applicable, e.g. "Mark as read" when everything is read). */
  visible?(context: ActionContext<T>): boolean;
  /** Shown but disabled when false (applicable, but not possible right now). */
  enabled?(context: ActionContext<T>): boolean;
  /**
   * Placeholder for a planned feature (or a missing server capability): shown disabled with a
   * "Planned" badge, never runs, not in the palette, shortcut not bound.
   */
  planned?: boolean;
  /** Ask first (T8). Returning undefined skips the confirmation for this context. */
  confirm?(context: ActionContext<T>): ActionConfirm | undefined;
  run(context: ActionContext<T>): void | Promise<void>;
}

export interface ActionRegistry<T> {
  readonly actions: readonly ActionDef<T>[];
  get(id: string): ActionDef<T> | undefined;
}

export class ActionRegistryError extends Error {
  override name = 'ActionRegistryError';
}

const surfacesOf = <T>(action: ActionDef<T>): readonly ActionSurface[] => action.surfaces ?? DEFAULT_SURFACES;

/**
 * Validates and freezes a list of actions. Throws on:
 * - duplicate ids (one definition per action),
 * - an action whose only menu path is the context menu (DESIGN.md §18 rule: every context-menu
 *   action is also reachable without right-click, via a toolbar button, the overflow menu or the
 *   item's own click).
 */
export function defineActions<T>(actions: readonly ActionDef<T>[]): ActionRegistry<T> {
  const byId = new Map<string, ActionDef<T>>();
  for (const action of actions) {
    if (byId.has(action.id)) throw new ActionRegistryError(`Duplicate action id "${action.id}".`);
    const surfaces = surfacesOf(action);
    if (surfaces.includes('context') && !action.defaultGesture && !surfaces.includes('overflow') && !surfaces.includes('toolbar')) {
      throw new ActionRegistryError(`Action "${action.id}" is only reachable by right-click; add the toolbar or overflow surface.`);
    }
    byId.set(action.id, action);
  }
  return { actions: [...actions], get: (id) => byId.get(id) };
}

/** An action evaluated for one context: what a surface renders and runs. */
export interface ResolvedAction<T = unknown> {
  readonly action: ActionDef<T>;
  readonly context: ActionContext<T>;
  readonly label: LabelRef;
  /** False when disabled or planned. */
  readonly enabled: boolean;
  readonly planned: boolean;
}

function fitsArity<T>(action: ActionDef<T>, count: number): boolean {
  return (action.arity ?? 'one') === 'one' ? count === 1 : count >= 1;
}

/**
 * The actions a surface shows for `targets`, in registry order. Filters by surface (or, for
 * `shortcut`, by having a shortcut), by arity and by `visible`; evaluates `enabled` and `planned`.
 */
export function resolveActions<T>(registry: ActionRegistry<T>, targets: readonly T[], source: ActionSource): ResolvedAction<T>[] {
  const context: ActionContext<T> = { targets, source };
  const resolved: ResolvedAction<T>[] = [];
  for (const action of registry.actions) {
    const offered = source === 'shortcut' ? action.shortcut !== undefined : surfacesOf(action).includes(source);
    if (!offered || !fitsArity(action, targets.length)) continue;
    if (action.visible && !action.visible(context)) continue;
    const planned = action.planned ?? false;
    const enabled = !planned && (action.enabled?.(context) ?? true);
    const label = action.labelFor?.(context) ?? action.label;
    resolved.push({ action, context, label, enabled, planned });
  }
  // The default gesture leads the context menu (Windows/Outlook convention).
  if (source === 'context') resolved.sort((a, b) => Number(Boolean(b.action.defaultGesture)) - Number(Boolean(a.action.defaultGesture)));
  return resolved;
}

/** Splits resolved actions into consecutive groups (menus draw a separator between them). */
export function groupActions<T>(actions: readonly ResolvedAction<T>[]): ResolvedAction<T>[][] {
  const groups: ResolvedAction<T>[][] = [];
  let current: string | undefined;
  for (const entry of actions) {
    if (entry.action.group !== current || groups.length === 0) {
      groups.push([]);
      current = entry.action.group;
    }
    groups[groups.length - 1]!.push(entry);
  }
  return groups;
}

/**
 * Targets of a right-click (Outlook behaviour): on a selected item the whole selection, on an
 * unselected item only that item, which then becomes the selection (`changed`).
 */
export function contextTargets(selected: readonly string[], targetId: string): { ids: string[]; changed: boolean } {
  if (selected.includes(targetId)) return { ids: [...selected], changed: false };
  return { ids: [targetId], changed: true };
}

export interface SelectionModifiers {
  /** Ctrl (⌘ on macOS): toggle one item. */
  toggle?: boolean;
  /** Shift: select the range from the anchor. */
  range?: boolean;
}

/**
 * Next selection of a list after a click (Explorer/Outlook model): plain click selects one,
 * Ctrl toggles, Shift selects the range between `anchorId` and `id` (in list order).
 */
export function nextSelection(order: readonly string[], selected: readonly string[], anchorId: string | undefined, id: string, modifiers: SelectionModifiers): string[] {
  if (modifiers.range && anchorId !== undefined) {
    const from = order.indexOf(anchorId);
    const to = order.indexOf(id);
    if (from >= 0 && to >= 0) return order.slice(Math.min(from, to), Math.max(from, to) + 1);
  }
  if (modifiers.toggle) return selected.includes(id) ? selected.filter((candidate) => candidate !== id) : [...selected, id];
  return [id];
}

/**
 * True when the browser's own menu should open instead of ours (DESIGN.md §18 per-surface rules):
 * Shift+right-click (escape hatch), text fields and editors, and text the user has selected.
 */
/** Marks a surface whose right-click always opens our menu (Shift+right-click still gives the browser's). */
export const NO_NATIVE_MENU_ATTRIBUTE = 'data-no-native-menu';

export function prefersNativeMenu(target: EventTarget | null, options: { shiftKey?: boolean; selection?: Selection | null } = {}): boolean {
  if (options.shiftKey) return true;
  if (!(target instanceof Element)) return true;
  if (target.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]')) return true;
  // Surfaces without selectable text (calendar grids): a stray text selection never brings the browser's menu back.
  if (target.closest(`[${NO_NATIVE_MENU_ATTRIBUTE}]`)) return false;
  const selection = options.selection;
  if (selection && !selection.isCollapsed && selection.rangeCount > 0) {
    for (let index = 0; index < selection.rangeCount; index += 1) {
      if (selection.getRangeAt(index).intersectsNode(target)) return true;
    }
  }
  return false;
}
